/**
 * Timeline del Historial (OpenSpec 008, Fase 3) — estilos según Figma CONTACTOS
 * (nodo 14639-48272). Reusable: <HistorySection module="order" entityId={incrementId} />.
 *
 * - Agrupado por día; rail + dots (antd Timeline). Entrada = actor (verde marca) +
 *   columna de módulo linkeable (solo vista transversal de Contactos) + frase (verbo
 *   bold, renderizada DE LAS PARTES) + hora + contador de grupo expandible.
 * - Notas con fondo dorado (#fffbe6 / #ffe58f), con pin si están fijadas.
 * - Agrupación en el render: ≥2 consecutivos del mismo generador (en Contactos, además
 *   mismo módulo) → colapsados con "N ^".
 * - Búsqueda (texto/persona, server-side trgm) con debounce; lazy load por keyset.
 * - Compose de notas y contador de actividades llegan en la Fase 4.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  DatePicker,
  Dropdown,
  Empty,
  Flex,
  Input,
  Modal,
  Popover,
  Spin,
  Timeline,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  AppstoreOutlined,
  ClockCircleOutlined,
  ContactsOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  MessageOutlined,
  MoreOutlined,
  PushpinOutlined,
  SearchOutlined,
  SendOutlined,
  CheckOutlined,
  CloseOutlined,
  SettingOutlined,
  ShopOutlined,
  UpOutlined,
  UserOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from "@ant-design/icons";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useGetIdentity } from "@refinedev/core";
import {
  HistoryEventNote,
  HistoryEventPayload,
  HistoryModule,
  HistoryNotePayload,
  HistoryRow,
  historyApi,
  historyNotesApi,
} from "@/services/history.api";
import { phraseParts, guestLabel, targetIsPerson } from "./history-i18n";
import { toDisplayText, toStoredText, mentionsInText } from "./mention-markers";
import { MentionsInput } from "./MentionsInput";
import {
  armarHilos,
  notasParaPedirHilo,
  sacarRespuestasDelStream,
  type HiloDeNota,
} from "./hilos-de-notas";
import { BRAND, MENTION_BG } from "./mention-style";
import { onDataMutation } from "@/utilities/mutation-bus";
import { axiosInstance } from "@/utilities";
import { API_URL_CONTACTS } from "@/services/XCONS_ENV";

const { Text } = Typography;

const PAGE = 30;

/** Techo de páginas que se piden buscando la nota de un deep-link (`?note=`). 10 × PAGE = 300. */
const FOCUS_MAX_PAGES = 10;

/** Borde de una nota mientras se edita: gris, para distinguirla de las que ya están guardadas. */
const EDITING_BORDER = "#d9d9d9";
const NOTE_BG = "#fffbe6";
const NOTE_BORDER = "#ffe58f";

/** Nota visible para el cliente (Q5): azul, para que no se confunda con la amarilla del equipo. */
const CUSTOMER_NOTE_BG = "#f0f5ff";
const CUSTOMER_NOTE_BORDER = "#adc6ff";
const CUSTOMER_NOTE_FG = "#2f54eb";

const MENTION_RE = /@\[([^\]]+)\]\((\d+)\)/g;

/**
 * Render del contenido de una nota: los marcadores `@[Nombre](id)` salen como `@Nombre` en el verde
 * de marca (rgb(21,116,75)), el mismo del contacto; el resto va como texto normal.
 *
 * La mención se pinta como CHIP (verde sobre un tinte del mismo verde), el gesto de Slack y
 * WhatsApp: el fondo es lo que la separa de la prosa a simple vista, porque sólo con color se
 * confunde con un texto enfatizado. `whiteSpace: nowrap` evita que un nombre con espacios se
 * corte en dos líneas y parezcan dos menciones.
 *
 * El marcador crudo sigue siendo la fuente de verdad: se guarda así, se edita así (el textarea de
 * Ant Mentions no renderiza chips) y `parseMentions` del backend lo lee para notificar.
 */
function renderNoteContent(content: string): (string | JSX.Element)[] | string {
  const parts: (string | JSX.Element)[] = [];
  let last = 0;
  for (const m of content.matchAll(MENTION_RE)) {
    const idx = m.index ?? 0;
    if (idx > last) parts.push(content.slice(last, idx));
    parts.push(
      <span
        key={idx}
        style={{
          color: BRAND,
          backgroundColor: MENTION_BG,
          fontStyle: "italic",
          fontWeight: 500,
          padding: "1px 4px",
          borderRadius: 4,
          whiteSpace: "nowrap",
        }}
      >
        @{m[1]}
      </span>,
    );
    last = idx + m[0].length;
  }
  if (last < content.length) parts.push(content.slice(last));
  return parts.length ? parts : content;
}

function actorStyle(actorType?: string): { icon: JSX.Element; color: string } {
  if (actorType === "operator" || actorType === "contact")
    return { icon: <UserOutlined />, color: BRAND };
  if (actorType === "guest") // checkout sin cuenta (Magento) — usuario atenuado, no brand
    return { icon: <UserOutlined />, color: "#8c8c8c" };
  if (actorType === "automation")
    return { icon: <ClockCircleOutlined />, color: "#595959" };
  return { icon: <AppstoreOutlined />, color: "#262626" }; // external / system
}

const BoxIcon = () => (
  <svg width="20" height="20" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ margin: 1 }}>
    <path d="M7.1395 1.78094C7.04996 1.74488 6.95004 1.74488 6.8605 1.78094L2.3845 3.57983L4.1875 4.30406L8.803 2.4494L7.1395 1.78094ZM9.8125 2.8556L5.197 4.71026L7 5.43449L11.6155 3.57983L9.8125 2.8556ZM12.25 4.13676L7.375 6.09617V12.0664L12.25 10.1069V4.13676ZM6.625 12.0671V6.09542L1.75 4.13676V10.1077L6.625 12.0671ZM6.58225 1.08083C6.85042 0.973058 7.14958 0.973058 7.41775 1.08083L12.7645 3.23015C12.834 3.25815 12.8936 3.30641 12.9356 3.36873C12.9775 3.43106 13 3.50458 13 3.57983V10.1077C12.9999 10.2583 12.9549 10.4054 12.8708 10.53C12.7867 10.6547 12.6674 10.7512 12.5283 10.8071L7.1395 12.973C7.04996 13.009 6.95004 13.009 6.8605 12.973L1.4725 10.8071C1.33319 10.7513 1.21371 10.6549 1.12948 10.5302C1.04526 10.4055 1.00015 10.2584 1 10.1077V3.57983C1.00002 3.50458 1.02246 3.43106 1.06443 3.36873C1.1064 3.30641 1.16598 3.25815 1.2355 3.23015L6.58225 1.08083Z" fill="currentColor" />
  </svg>
);

const QuoteIcon = () => (
  <svg width="22" height="22" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10.5445 7.40342V2.65126C10.5445 2.47935 10.4763 2.31442 10.3549 2.19242C10.2334 2.07042 10.0685 2.00126 9.89617 2.00002H3.15775C3.0716 1.99939 2.98617 2.01578 2.9064 2.04823C2.82662 2.08068 2.75408 2.12855 2.69294 2.18908C2.63179 2.24962 2.58327 2.32162 2.55016 2.40094C2.51704 2.48026 2.5 2.56534 2.5 2.65126V10.4001C2.50062 10.4856 2.51812 10.5702 2.55151 10.6489C2.58491 10.7277 2.63353 10.7992 2.69461 10.8592C2.75568 10.9192 2.82802 10.9667 2.90749 10.9988C2.98696 11.031 3.072 11.0472 3.15775 11.0466H6.88186C7.11184 11.3273 7.39823 11.5569 7.72259 11.7205C8.04695 11.8842 8.40208 11.9782 8.76511 11.9966C9.12814 12.0151 9.49101 11.9575 9.83035 11.8275C10.1697 11.6976 10.478 11.4982 10.7353 11.2422C10.9927 10.9862 11.1934 10.6792 11.3246 10.3412C11.4558 10.0031 11.5145 9.64135 11.497 9.27926C11.4794 8.91718 11.386 8.56277 11.2228 8.23888C11.0596 7.91498 10.8301 7.62878 10.5492 7.3987L10.5445 7.40342ZM3.15775 10.5747C3.11169 10.5735 3.06785 10.5547 3.03527 10.5222C3.0027 10.4897 2.98386 10.446 2.98267 10.4001V2.65126C2.98386 2.60532 3.0027 2.5616 3.03527 2.52911C3.06785 2.49662 3.11169 2.47784 3.15775 2.47665H9.89617C9.94386 2.47665 9.9896 2.49554 10.0233 2.52917C10.057 2.5628 10.076 2.60842 10.076 2.65598V7.09196C9.58654 6.83827 9.02883 6.74652 8.48361 6.83002C7.9384 6.91351 7.43398 7.1679 7.04341 7.55636C6.65284 7.94481 6.39639 8.44716 6.31119 8.99066C6.226 9.53417 6.31649 10.0906 6.56955 10.5794L3.15775 10.5747ZM8.88825 11.5185C8.40702 11.5163 7.94109 11.3497 7.56801 11.0466C7.39652 10.9128 7.24652 10.7536 7.1232 10.5747C6.85097 10.1658 6.72918 9.67546 6.77854 9.18711C6.82789 8.69877 7.04534 8.24255 7.39391 7.89605C7.74247 7.54954 8.20064 7.33415 8.69047 7.28649C9.18031 7.23884 9.67157 7.36187 10.0807 7.63466C10.2623 7.76623 10.422 7.92548 10.5539 8.10657C10.8555 8.4763 11.0224 8.93718 11.0271 9.41377C11.0221 9.97617 10.7936 10.5136 10.3917 10.9082C9.98987 11.3028 9.44746 11.5223 8.88351 11.5185H8.88825Z" fill="currentColor" />
    <path d="M3.81551 4.95891H5.70832C5.77107 4.95891 5.83125 4.93405 5.87562 4.8898C5.91999 4.84555 5.94492 4.78554 5.94492 4.72296C5.94492 4.66038 5.91999 4.60036 5.87562 4.55611C5.83125 4.51186 5.77107 4.487 5.70832 4.487H3.81551C3.75275 4.487 3.69257 4.51186 3.6482 4.55611C3.60383 4.60036 3.5789 4.66038 3.5789 4.72296C3.5789 4.78554 3.60383 4.84555 3.6482 4.8898C3.69257 4.93405 3.75275 4.95891 3.81551 4.95891Z" fill="currentColor" />
    <path d="M5.72252 7.72433H5.01271V6.99758C5.01271 6.935 4.98778 6.87498 4.94341 6.83073C4.89904 6.78648 4.83886 6.76162 4.77611 6.76162C4.71336 6.76162 4.65318 6.78648 4.60881 6.83073C4.56443 6.87498 4.53951 6.935 4.53951 6.99758V7.70545H3.8297C3.76695 7.70545 3.70677 7.73031 3.6624 7.77456C3.61803 7.81881 3.5931 7.87883 3.5931 7.9414C3.5931 8.00398 3.61803 8.064 3.6624 8.10825C3.70677 8.1525 3.76695 8.17736 3.8297 8.17736H4.53951V8.88523C4.53951 8.94781 4.56443 9.00783 4.60881 9.05208C4.65318 9.09633 4.71336 9.12119 4.77611 9.12119C4.83886 9.12119 4.89904 9.09633 4.94341 9.05208C4.98778 9.00783 5.01271 8.94781 5.01271 8.88523V8.17736H5.72252C5.78527 8.17736 5.84545 8.1525 5.88982 8.10825C5.93419 8.064 5.95912 8.00398 5.95912 7.9414C5.95912 7.87883 5.93419 7.81881 5.88982 7.77456C5.84545 7.73031 5.78527 7.70545 5.72252 7.70545V7.72433Z" fill="currentColor" />
    <path d="M7.34087 5.58184C7.36286 5.60396 7.38903 5.62151 7.41787 5.63349C7.4467 5.64547 7.47762 5.65164 7.50886 5.65164C7.54009 5.65164 7.57102 5.64547 7.59985 5.63349C7.62868 5.62151 7.65485 5.60396 7.67684 5.58184L8.17844 5.08161L8.68004 5.58184C8.72459 5.62627 8.78501 5.65124 8.84802 5.65124C8.91103 5.65124 8.97146 5.62627 9.01601 5.58184C9.06056 5.53741 9.08559 5.47715 9.08559 5.41431C9.08559 5.35148 9.06056 5.29122 9.01601 5.24678L8.51441 4.74184L9.01601 4.24161C9.06056 4.19718 9.08559 4.13692 9.08559 4.07408C9.08559 4.01124 9.06056 3.95098 9.01601 3.90655C8.97146 3.86212 8.91103 3.83716 8.84802 3.83716C8.78501 3.83716 8.72459 3.86212 8.68004 3.90655L8.17844 4.4115L7.67684 3.91127C7.63229 3.86684 7.57186 3.84188 7.50886 3.84188C7.44585 3.84188 7.38542 3.86684 7.34087 3.91127C7.29632 3.9557 7.27129 4.01596 7.27129 4.0788C7.27129 4.14163 7.29632 4.2019 7.34087 4.24633L7.84247 4.74184L7.34087 5.24207C7.31797 5.26408 7.29976 5.29047 7.28732 5.31966C7.27488 5.34885 7.26846 5.38024 7.26846 5.41195C7.26846 5.44367 7.27488 5.47506 7.28732 5.50425C7.29976 5.53344 7.31797 5.55983 7.34087 5.58184Z" fill="currentColor" />
    <path d="M9.82992 8.73422H7.93711C7.87435 8.73422 7.81417 8.75908 7.7698 8.80333C7.72543 8.84758 7.7005 8.9076 7.7005 8.97018C7.7005 9.03276 7.72543 9.09277 7.7698 9.13702C7.81417 9.18127 7.87435 9.20613 7.93711 9.20613H9.82992C9.89267 9.20613 9.95285 9.18127 9.99722 9.13702C10.0416 9.09277 10.0665 9.03276 10.0665 8.97018C10.0665 8.9076 10.0416 8.84758 9.99722 8.80333C9.95285 8.75908 9.89267 8.73422 9.82992 8.73422Z" fill="currentColor" />
    <path d="M9.82992 9.56007H7.93711C7.87435 9.56007 7.81417 9.58493 7.7698 9.62918C7.72543 9.67343 7.7005 9.73345 7.7005 9.79603C7.7005 9.85861 7.72543 9.91862 7.7698 9.96287C7.81417 10.0071 7.87435 10.032 7.93711 10.032H9.82992C9.89267 10.032 9.95285 10.0071 9.99722 9.96287C10.0416 9.91862 10.0665 9.85861 10.0665 9.79603C10.0665 9.73345 10.0416 9.67343 9.99722 9.62918C9.95285 9.58493 9.89267 9.56007 9.82992 9.56007Z" fill="currentColor" />
  </svg>
);

/** Meta por módulo para la columna de la vista transversal de Contactos. */
const MODULE_META: Record<string, { icon: JSX.Element; en: string; es: string }> = {
  quote: { icon: <QuoteIcon />, en: "Quote", es: "Presupuesto" },
  order: { icon: <BoxIcon />, en: "Order", es: "Pedido" },
  conversation: { icon: <MessageOutlined />, en: "Conversation", es: "Conversación" },
  contact: { icon: <ContactsOutlined />, en: "Contact", es: "Contacto" },
  // Settings de vendedor/tienda (shipping/payment config, store-linked-seller): no
  // tienen ficha propia, se ven en la del contacto que los hizo.
  seller: { icon: <SettingOutlined />, en: "Settings", es: "Configuración" },
  store: { icon: <ShopOutlined />, en: "Store", es: "Store" },
  // Mismo icono que el Catálogo en el menú: la columna nombra la sección a la que lleva.
  product: { icon: <AppstoreOutlined />, en: "Product", es: "Producto" },
};

/** Identificador o nombre de la entidad para la columna de módulo en vista Contactos. */
function entityLabel(p: HistoryEventPayload): string | null {
  switch (p.module) {
    case "quote":
      return p.entityId ? `#${p.entityId}` : null;
    case "order":
      // `entityId` del pedido es el id interno; el número que se muestra es el increment_id.
      return p.orderIncrementId || p.entityId ? `#${p.orderIncrementId ?? p.entityId}` : null;
    // El canal, donde un presupuesto muestra `#322367` (022 H2a). El `entityId` de una conversación
    // es el uuid del hilo: no le sirve a nadie, y por eso hasta ahora no se mostraba nada.
    // Los eventos anteriores al 022 no traen `channelName` → subtítulo vacío, nunca el uuid.
    case "conversation":
      return (p.metadata?.channelName as string) || null;
    case "contact": {
      const name = (p.metadata?.name as string) || (p.metadata?.contactName as string) || null;
      return name || null;
    }
    // El SKU, que es la clave del catálogo y lo que reconoce el operador. `entityId` puede ser
    // el `entity_id` de Magento (los eventos posteriores a publicar), así que se usa el
    // `productSku` que resuelve el backend, con el metadata como respaldo.
    case "product":
      return p.productSku || (p.metadata?.sku as string) || null;
    default:
      return null;
  }
}

/**
 * Frase desde las partes: verbo en bold + resto, traducida al locale actual.
 *
 * Si el `target` es el contacto del registro —en un mensaje de conversación, con quién se habla—
 * se renderiza como link a su ficha. Mismo criterio que el actor: no linkea cuando ya estás parado
 * en esa ficha, porque el link no llevaría a ningún lado.
 */
/**
 * Link al mensaje de la conversación del que salió un evento (change
 * `mensaje-con-link-al-presupuesto`, Fase 5).
 *
 * Los eventos que nacen de un mensaje —"Envió un WhatsApp a Roberto", "Compartió el Presupuesto
 * #N a Roberto"— llevan en `metadata` la conversación y el mensaje, y el chat sabe abrirse parado
 * ahí. Sin esto la entrada del historial era texto muerto: decía que el presupuesto se compartió,
 * pero no había forma de ir a ver el mensaje. En el timeline de la propia conversación no se
 * linkea, que es donde ya estás.
 */
function conversationMessageHref(row: HistoryRow, viewModule?: HistoryModule): string | null {
  if (row.kind !== "event" || viewModule === "conversation") return null;
  const meta = (row.payload.metadata ?? {}) as { conversationId?: unknown; messageId?: unknown };
  if (typeof meta.conversationId !== "string" || !meta.conversationId) return null;
  const params = new URLSearchParams({ open: meta.conversationId });
  if (typeof meta.messageId === "string" && meta.messageId) params.set("message", meta.messageId);
  return `/conversations?${params.toString()}`;
}

/**
 * El id con el que la ruta `/orders/:id` abre el pedido de un evento. `entityId` YA es el id
 * interno; el fallback a `metadata.orderId` cubre los eventos viejos, grabados cuando el
 * entity_id era el increment_id (ver backfill-order-entity-id.ts).
 */
function orderRouteId(row: HistoryRow): string | null {
  if (row.kind !== "event") return null;
  const { entityId } = row.payload;
  if (entityId && /^\d{1,9}$/.test(entityId)) return entityId;
  const legacyId = (row.payload.metadata as { orderId?: number } | null)?.orderId;
  return legacyId != null ? String(legacyId) : null;
}

/**
 * La entrega que nombra la frase ("Cambió estado a Entregado de **Entrega 1**") → su panel en el
 * detalle del pedido (xcons-docs#583). "Entrega 1" es el número visible, no el id: el panel se
 * busca por `metadata.shipmentId`, y sin él no se linkea. Parado en ese mismo pedido, el link
 * hace scroll al panel en vez de navegar.
 */
function shipmentLink(
  row: HistoryRow,
  viewModule?: HistoryModule,
  entityId?: string | number,
): { href: string; panelId: string; samePage: boolean } | null {
  if (row.kind !== "event" || row.payload.module !== "order") return null;
  if (!/^Delivery \d+$/.test((row.payload.target ?? "").trim())) return null;
  const shipmentId = (row.payload.metadata as { shipmentId?: unknown } | null)?.shipmentId;
  if (shipmentId == null || shipmentId === "") return null;
  const orderId = orderRouteId(row);
  if (!orderId) return null;
  const panelId = `shipment-panel-${shipmentId}`;
  return {
    href: `/orders/${orderId}#${panelId}`,
    panelId,
    samePage: viewModule === "order" && orderId === String(entityId),
  };
}

function Phrase({
  row,
  viewModule,
  entityId,
}: {
  row: HistoryRow;
  viewModule?: HistoryModule;
  entityId?: string | number;
}) {
  const { i18n } = useTranslation();
  const es = (i18n.language || "").toLowerCase().startsWith("es");
  const { verb, rest, target } = phraseParts(row, i18n.language);
  const messageHref = conversationMessageHref(row, viewModule);
  // El target linkea a la ficha del contacto sólo cuando ES el contacto (el destinatario de un
  // envío); una entrega linkea a su panel del pedido; cualquier otra cosa va como texto.
  const targetContactId =
    row.kind === "event" && targetIsPerson(row.payload.conn2, row.payload.target)
      ? row.payload.contactId
      : null;
  const shipment = shipmentLink(row, viewModule, entityId);
  const targetHref = shipment
    ? shipment.href
    : target && targetContactId && !(viewModule === "contact" && String(targetContactId) === String(entityId))
      ? `/contacts/${targetContactId}`
      : null;
  // Nota (Figma CONTACTOS): contenido en itálica, sin la palabra "Nota"; menciones @[Nombre](id)
  // → @Nombre.
  if (row.kind === "note") {
    // Una nota publicada se marca en el propio timeline: el operador tiene que poder distinguir de
    // un vistazo lo que el cliente está leyendo de lo que es conversación interna del equipo.
    return (
      <Text italic style={{ fontSize: 12 }}>
        {renderNoteContent(rest)}
      </Text>
    );
  }
  // El target va como link sin partir el string a mano: se corta en su única aparición, y si no
  // se encuentra (traducción, dato libre) se cae a la frase plana.
  const corte = targetHref && target ? rest.indexOf(target) : -1;
  return (
    <Text style={{ fontSize: 12 }}>
      <b>{verb}</b>{" "}
      {corte >= 0 && target ? (
        <>
          {rest.slice(0, corte)}
          <Link
            to={targetHref!}
            onClick={(e) => {
              e.stopPropagation();
              if (!shipment?.samePage) return;
              e.preventDefault();
              document
                .getElementById(shipment.panelId)
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          >
            {target}
          </Link>
          {rest.slice(corte + target.length)}
        </>
      ) : (
        rest
      )}
      {messageHref && (
        <Tooltip title={es ? "Ver el mensaje en la conversación" : "Open the message in the conversation"}>
          <Link
            to={messageHref}
            onClick={(e) => e.stopPropagation()}
            style={{ marginLeft: 6, whiteSpace: "nowrap" }}
            aria-label={es ? "Ver el mensaje en la conversación" : "Open the message in the conversation"}
          >
            <MessageOutlined style={{ fontSize: 12 }} />
          </Link>
        </Tooltip>
      )}
    </Text>
  );
}

/**
 * Link al registro origen para la vista transversal de Contactos (Fase 3): cada
 * entrada del timeline lleva al detalle de su módulo. Los eventos del propio
 * contacto no linkean (ya estás en su ficha).
 */
function originHref(row: HistoryRow, viewModule: HistoryModule): string | null {
  if (viewModule !== "contact" || row.kind !== "event") return null;
  const { module, entityId } = row.payload;
  if (!entityId) return null;
  switch (module) {
    case "quote":
      return `/quotes/${entityId}`;
    case "order": {
      const orderId = orderRouteId(row);
      return orderId ? `/orders/${orderId}` : null;
    }
    case "conversation": {
      // Con `message` el chat se abre parado en el mensaje del que salió el evento (change
      // `mensaje-con-link-al-presupuesto`, Fase 5).
      const messageId = (row.payload.metadata as { messageId?: string } | null)?.messageId;
      return `/conversations?open=${entityId}${
        messageId ? `&message=${encodeURIComponent(messageId)}` : ""
      }`;
    }
    case "product": {
      // La ruta del catálogo va por SKU, no por id: `entityId` sirve sólo cuando el evento ya
      // está anclado en el SKU (un borrador). Sin SKU no se linkea, que es mejor que un 404.
      const sku =
        row.payload.productSku || (row.payload.metadata?.sku as string) || null;
      return sku ? `/catalog/${encodeURIComponent(sku)}` : null;
    }
    default:
      return null; // contact → misma ficha, sin link
  }
}

interface Group {
  key: string;
  rows: HistoryRow[];
}

/** Agrupa consecutivos del mismo generador (y módulo, si aplica) dentro del stream. */
function groupRows(rows: HistoryRow[], byModule: boolean): Group[] {
  const groups: Group[] = [];
  for (const row of rows) {
    const last = groups[groups.length - 1];
    const sameActor =
      last &&
      last.rows[0].kind === "event" &&
      row.kind === "event" &&
      last.rows[0].payload.actorName === row.payload.actorName &&
      (!byModule || last.rows[0].payload.module === row.payload.module) &&
      dayjs(last.rows[0].occurredAt).isSame(row.occurredAt, "day");
    if (sameActor) last.rows.push(row);
    else groups.push({ key: row.id, rows: [row] });
  }
  return groups;
}

const CONTACT_PILLS: { value: string; label: string }[] = [
  { value: "contact", label: "Contactos" },
  { value: "quote", label: "Presupuestos" },
  { value: "order", label: "Pedidos" },
  { value: "conversation", label: "Conversaciones" },
  { value: "product", label: "Productos" },
  { value: "note", label: "Notas" },
];

export const HistorySection = ({
  module,
  entityId,
  externalQ,
  scrollable = false,
  notes = true,
}: {
  module: HistoryModule;
  entityId: string | number;
  externalQ?: string;
  scrollable?: boolean;
  /**
   * ¿El registro admite notas? Apagado deja el timeline **de sólo lectura**: sin compose, sin
   * las secciones de fijadas y programadas, y sin el "Agregar nota" de cada entrada.
   *
   * Lo apaga el **producto** (PIM Fase 12), y no por una decisión de UI: una nota se graba con
   * un `store_code` NOT NULL y sus mencionables se resuelven por store/seller, y un producto no
   * tiene ninguno de los dos —el catálogo es global—. Los cuatro endpoints de notas contestan
   * 404 para `module=product`; encenderlas es una decisión de modelo, no un prop.
   */
  notes?: boolean;
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { i18n } = useTranslation();
  const es = (i18n.language || "").toLowerCase().startsWith("es");
  // Pills de módulo — solo en la vista transversal de Contactos (doc de producto).
  const [pill, setPill] = useState("");
  const filter = module === "contact" && pill ? pill : undefined;
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Compose de notas (Fase 4): enviar ahora o programar (popover con fecha/hora).
  const [compose, setCompose] = useState("");
  const [sending, setSending] = useState(false);
  /** El operador marcó esta nota como visible para el cliente (sólo presupuestos). */
  const [visibleOnFront, setVisibleOnFront] = useState(false);
  const [schedOpen, setSchedOpen] = useState(false);
  const [schedDate, setSchedDate] = useState<Dayjs | null>(null);
  // Autocomplete @ de menciones: operadores con acceso al registro.
  const [mentionables, setMentionables] = useState<{ userId: number; name: string }[]>([]);
  // Solo el autor puede editar/fijar/eliminar su nota (lo valida también el backend).
  const { data: identity } = useGetIdentity<{ id?: number }>();
  const currentUserId = identity?.id;
  // `text` es el texto LEGIBLE que se edita; `raw` es el contenido original con marcadores,
  // para poder resolver una mención a alguien que ya no está en la lista de mencionables.
  const [editing, setEditing] = useState<{ noteId: string; text: string; raw: string } | null>(
    null,
  );
  const [savingEdit, setSavingEdit] = useState(false);
  // Programadas pendientes del registro: sección propia arriba, no entran al timeline.
  const [scheduled, setScheduled] = useState<HistoryNotePayload[]>([]);
  // Fijadas: idem, van primeras en el log y el backend las saca del stream.
  const [pinnedNotes, setPinnedNotes] = useState<HistoryNotePayload[]>([]);
  /**
   * Hilos de las notas generales (D66): el asistente contesta en el hilo de la nota que lo arrobó,
   * y el timeline trae esa respuesta como otra nota general. El hilo de cada nota que arroba a
   * alguien se pide aparte, por la nota a la que se le pidió. `hilosPedidosRef` evita pedirlo dos
   * veces en la misma carga; `hilosGenRef` descarta lo que llega de una carga anterior.
   */
  const [hilos, setHilos] = useState<Map<string, HiloDeNota>>(new Map());
  const hilosPedidosRef = useRef(new Set<string>());
  const hilosGenRef = useRef(0);
  /**
   * Deep-link a una nota (`?note=<noteId>`, lo pone la campanita de notificaciones): hay que
   * hacerle foco. `focusNoteId` es el objetivo pendiente y se limpia en cuanto se encuentra;
   * `highlightNoteId` es el resaltado temporal que se ve después.
   */
  const [focusNoteId, setFocusNoteId] = useState<string | null>(null);
  const [highlightNoteId, setHighlightNoteId] = useState<string | null>(null);
  const noteRefs = useRef(new Map<string, HTMLElement>());
  /**
   * Páginas pedidas buscando la nota del deep-link. Con techo: una nota vieja en un registro con
   * mucha actividad podría paginar el historial entero de 30 en 30. Si no aparece en
   * FOCUS_MAX_PAGES, se abandona la búsqueda y el timeline queda como una carga normal — mejor eso
   * que 100 requests para hacer un scroll.
   */
  const focusPagesRef = useRef(0);
  // Nota por-log: adjuntar a un evento (eventId) o responder en un hilo (parentNoteId).
  // El compose se expande INLINE dentro del timeline (Figma 16497-95013), no en un modal (#139).
  const [noteDraft, setNoteDraft] = useState<{
    eventId?: string;
    parentNoteId?: string;
    text: string;
  } | null>(null);
  const [savingNoteDraft, setSavingNoteDraft] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false);

  // Debounce de búsqueda (server-side, respeta permisos en el backend).
  useEffect(() => {
    if (externalQ !== undefined) return;
    const t = setTimeout(() => setQ(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search, externalQ]);

  // Sync búsqueda externa (cuando el buscador vive en el header del Card padre).
  useEffect(() => {
    if (externalQ !== undefined) setQ(externalQ);
  }, [externalQ]);

  const load = useCallback(
    async (before?: string) => {
      if (loadingRef.current) return;
      loadingRef.current = true;
      setLoading(true);
      // Desde la primera página, los hilos se vuelven a pedir: la respuesta del asistente pudo
      // llegar después de la carga anterior.
      if (!before) {
        hilosPedidosRef.current = new Set();
        hilosGenRef.current += 1;
      }
      try {
        const page = await historyApi.timeline(module, entityId, {
          before,
          q: q || undefined,
          filter,
          limit: PAGE,
        });
        setRows((prev) => (before ? [...prev, ...page] : page));
        setHasMore(page.length === PAGE);
      } catch {
        if (!before) setRows([]);
        setHasMore(false);
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [module, entityId, q, filter],
  );

  // Carga inicial / al cambiar búsqueda, filtro o registro.
  useEffect(() => {
    setRows([]);
    setHasMore(true);
    void load();
  }, [load]);

  // Candidatos al autocomplete @ (una vez por registro). Sin notas no hay a quién mencionar.
  useEffect(() => {
    if (!notes) return;
    let alive = true;
    historyNotesApi
      .mentionables(module, entityId)
      .then((ops) => alive && setMentionables(ops))
      .catch(() => alive && setMentionables([]));
    return () => {
      alive = false;
    };
  }, [module, entityId, notes]);

  const mentionOptions = useMemo(
    () =>
      // `value` es lo que antd inserta en el textarea: el NOMBRE, para que se lea
      // "@Martita Jaja" y no el marcador crudo. El marcador se reconstruye al enviar
      // (toStoredText) — ver mention-markers.ts.
      mentionables.map((m) => ({
        value: m.name,
        label: m.name,
        key: String(m.userId),
      })),
    [mentionables],
  );

  /** Nombres que el espejo del input pinta como chip. */
  const mentionNames = useMemo(() => mentionables.map((m) => m.name), [mentionables]);

  // Lazy load: sentinel al final del timeline.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && hasMore && !loadingRef.current && rows.length) {
        void load(rows[rows.length - 1].occurredAt);
      }
    }, scrollable ? { root: scrollContainerRef.current } : undefined);
    obs.observe(el);
    return () => obs.disconnect();
  }, [rows, hasMore, load, scrollable]);

  // Deep-link `?note=`: se lee una vez al montar y se consume. Se saca de la URL enseguida para
  // que un refresh o un back no vuelva a disparar el scroll.
  useEffect(() => {
    const target = searchParams.get("note");
    if (!target) return;
    setFocusNoteId(target);
    const next = new URLSearchParams(searchParams);
    next.delete("note");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Hilos de las notas generales: se le pide el hilo a cada nota que arroba a alguien —en el
  // stream, fijada o programada—, una vez por carga. Un 403 (la ve por estar arrobado, sin acceso
  // a la ficha) o cualquier error la deja como estaba: sin hilo, igual que antes.
  useEffect(() => {
    if (!notes) return;
    const ids = notasParaPedirHilo(
      [
        ...rows
          .filter((r) => r.kind === "note")
          .map((r) => ({
            noteId: r.id,
            mentions: r.payload.mentions,
            parentNoteId: r.payload.parentNoteId,
          })),
        ...pinnedNotes,
        ...scheduled,
      ],
      hilosPedidosRef.current,
    );
    const gen = hilosGenRef.current;
    for (const id of ids) {
      hilosPedidosRef.current.add(id);
      historyNotesApi
        .thread(id)
        .then((h) => {
          if (gen === hilosGenRef.current) setHilos((prev) => new Map(prev).set(id, h));
        })
        .catch(() => undefined);
    }
  }, [rows, pinnedNotes, scheduled, notes]);

  const hilosArmados = useMemo(() => armarHilos([...hilos.values()], rows), [hilos, rows]);

  /** Notas generales en pantalla: las que pueden llevar su hilo debajo. */
  const raicesEnPantalla = useMemo(
    () =>
      new Set([
        ...rows.filter((r) => r.kind === "note").map((r) => r.id),
        ...pinnedNotes.map((n) => n.noteId),
        ...scheduled.map((n) => n.noteId),
      ]),
    [rows, pinnedNotes, scheduled],
  );

  /** El stream sin las respuestas que ya van debajo de su nota. */
  const filasVisibles = useMemo(
    () => sacarRespuestasDelStream(rows, hilosArmados, raicesEnPantalla),
    [rows, hilosArmados, raicesEnPantalla],
  );

  /**
   * Foco en la nota del deep-link. La nota puede estar en cuatro lugares distintos, y hay que
   * mirarlos todos:
   *   1. el bloque de FIJADAS (que el backend saca del stream — sin esto paginaría al infinito
   *      buscando una nota que nunca va a venir),
   *   2. el bloque de PROGRAMADAS (tampoco está en el stream hasta que vence),
   *   3. una fila propia del timeline (nota general),
   *   4. anidada bajo un evento (nota por-log) — las menciones también viven ahí.
   *
   * Si no está en lo cargado y hay más páginas, se sigue paginando. El corte por `hasMore` es lo
   * que evita el loop cuando el noteId no existe o ya se borró.
   */
  useEffect(() => {
    if (!focusNoteId) return;
    const raizDelFoco = hilosArmados.raizDe.get(focusNoteId);
    const inBlocks =
      pinnedNotes.some((n) => n.noteId === focusNoteId) ||
      scheduled.some((n) => n.noteId === focusNoteId) ||
      // Una respuesta del asistente, dibujada debajo de su nota general (D66).
      (!!raizDelFoco && raicesEnPantalla.has(raizDelFoco));
    const inRows = rows.some(
      (r) =>
        r.id === focusNoteId ||
        (r.kind === "event" && (r.payload.notes ?? []).some((n) => n.noteId === focusNoteId)),
    );
    if (!inBlocks && !inRows) {
      // Todavía no llegó: pedir la página siguiente. Se abandona si no hay más páginas o si ya se
      // gastó el techo de intentos.
      if (!hasMore || focusPagesRef.current >= FOCUS_MAX_PAGES) {
        setFocusNoteId(null);
      } else if (!loadingRef.current && rows.length) {
        focusPagesRef.current += 1;
        void load(rows[rows.length - 1].occurredAt);
      }
      return;
    }
    // Está renderizada: scroll + resaltado temporal. El rAF espera al layout del render actual.
    const id = focusNoteId;
    setFocusNoteId(null);
    requestAnimationFrame(() => {
      noteRefs.current.get(id)?.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightNoteId(id);
    });
  }, [focusNoteId, rows, pinnedNotes, scheduled, hasMore, load, hilosArmados, raicesEnPantalla]);

  // El resaltado es una ayuda visual, no un estado: se apaga solo.
  useEffect(() => {
    if (!highlightNoteId) return;
    const t = setTimeout(() => setHighlightNoteId(null), 4000);
    return () => clearTimeout(t);
  }, [highlightNoteId]);

  /** Registra la caja de una nota para poder hacerle scroll cuando llegue por deep-link. */
  const noteFocusRef = (noteId: string) => (el: HTMLDivElement | null) => {
    if (el) noteRefs.current.set(noteId, el);
    else noteRefs.current.delete(noteId);
  };

  /** Marco del resaltado temporal. Se mergea sobre el style propio de cada caja de nota. */
  const highlightStyle = (noteId: string) =>
    highlightNoteId === noteId
      ? { outline: `2px solid ${BRAND}`, outlineOffset: 2 }
      : null;

  // Día → grupos de consecutivos.
  const days = useMemo(() => {
    const byDay = new Map<string, HistoryRow[]>();
    for (const r of filasVisibles) {
      const day = dayjs(r.occurredAt).format("YYYY-MM-DD");
      const list = byDay.get(day) ?? [];
      list.push(r);
      byDay.set(day, list);
    }
    return [...byDay.entries()].map(([day, list]) => ({
      day,
      groups: groupRows(list, module === "contact"),
    }));
  }, [filasVisibles, module]);

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Programadas pendientes y fijadas: ninguna de las dos viene en el timeline (el backend las
  // excluye del stream), se piden aparte para las secciones de arriba.
  const loadScheduled = useCallback(() => {
    historyNotesApi
      .scheduled(module, entityId)
      .then(setScheduled)
      .catch(() => setScheduled([]));
  }, [module, entityId]);

  const loadPinned = useCallback(() => {
    historyNotesApi
      .pinned(module, entityId)
      .then(setPinnedNotes)
      .catch(() => setPinnedNotes([]));
  }, [module, entityId]);

  useEffect(() => {
    if (!notes) return;
    loadScheduled();
    loadPinned();
  }, [loadScheduled, loadPinned, notes]);

  // Recarga desde la primera página (una nota nueva/editada queda arriba).
  const reload = useCallback(() => {
    setRows([]);
    setHasMore(true);
    void load();
    // Fijar/desfijar y programar mueven notas ENTRE el stream y estas secciones, así que las tres
    // listas se refrescan juntas: si no, una nota recién fijada queda duplicada hasta el próximo
    // montaje del componente.
    loadScheduled();
    loadPinned();
  }, [load, loadScheduled, loadPinned]);

  /**
   * Refresco en vivo: cualquier acción que escriba en el backend recarga el timeline, sin que el
   * operador tenga que recargar la página.
   *
   * El aviso viene del interceptor de `axiosInstance` (ver utilities/mutation-bus). Cubre todo lo
   * que pasa por ahí: agregar una relación o un grupo al contacto, editar sus datos, y cualquier
   * acción sobre un presupuesto o un pedido —incluidos los permisos— porque el dataProvider de
   * Refine usa el mismo axios. No hay que enchufar nada en cada acción.
   *
   * El debounce es necesario, no cosmético: varias acciones disparan un `Promise.all` de requests
   * (el guardado de participaciones manda un POST por fila cambiada), y sin esto cada uno pediría
   * su propia recarga.
   */
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const off = onDataMutation(() => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => reload(), 600);
    });
    return () => {
      if (timer) clearTimeout(timer);
      off();
    };
  }, [reload]);

  // Crea la nota (general) y recarga.
  /**
   * Nota visible para el cliente (Q5). Sólo en presupuestos: es el único front donde el cliente
   * puede verla. Arranca en falso — una nota es interna salvo que el operador la publique a
   * propósito.
   */
  const permiteNotaVisible = module === "quote";
  const submitNote = useCallback(
    async (scheduledFor?: string) => {
      const content = toStoredText(compose.trim(), mentionables);
      if (!content || sending) return;
      setSending(true);
      try {
        await historyNotesApi.create(module, entityId, {
          content,
          ...(scheduledFor ? { scheduledFor } : {}),
          ...(visibleOnFront ? { visibleOnFront: true } : {}),
        });
        setCompose("");
        setSchedOpen(false);
        setSchedDate(null);
        setVisibleOnFront(false);
        message.success(es ? "Nota agregada" : "Note added");
        reload();
      } catch {
        message.error(es ? "No se pudo agregar la nota" : "Could not add the note");
      } finally {
        setSending(false);
      }
    },
    // `visibleOnFront` va en las dependencias: sin él el callback se queda con el valor del primer
    // render y la nota se creaba siempre como interna, aunque el input se viera azul.
    [compose, sending, module, entityId, es, reload, mentionables, visibleOnFront],
  );

  const togglePin = useCallback(
    async (noteId: string, pinned: boolean) => {
      try {
        await historyNotesApi.patch(noteId, { pinned: !pinned });
        reload();
      } catch {
        message.error(es ? "No se pudo actualizar" : "Could not update");
      }
    },
    [reload, es],
  );

  /**
   * Publica o despublica la nota para el cliente (Q5). Sin confirmación: es reversible de un click,
   * y pedir confirmación en cada toggle molesta más de lo que protege.
   */
  const toggleVisible = useCallback(
    async (noteId: string, visibleOnFront: boolean) => {
      try {
        await historyNotesApi.patch(noteId, { visibleOnFront: !visibleOnFront });
        message.success(
          visibleOnFront
            ? es
              ? "La nota ya no la ve el cliente"
              : "The customer no longer sees this note"
            : es
              ? "La nota ahora la ve el cliente"
              : "The customer can now see this note",
        );
        reload();
      } catch {
        message.error(es ? "No se pudo actualizar" : "Could not update");
      }
    },
    [reload, es],
  );

  const deleteNote = useCallback(
    (noteId: string) => {
      Modal.confirm({
        title: es ? "¿Eliminar la nota?" : "Delete note?",
        okText: es ? "Eliminar" : "Delete",
        okButtonProps: { danger: true },
        cancelText: es ? "Cancelar" : "Cancel",
        onOk: async () => {
          try {
            await historyNotesApi.remove(noteId);
            reload();
          } catch {
            message.error(es ? "No se pudo eliminar" : "Could not delete");
          }
        },
      });
    },
    [reload, es],
  );

  const saveEdit = useCallback(async () => {
    if (!editing || !editing.text.trim()) return;
    setSavingEdit(true);
    try {
      // Las menciones que ya tenía la nota se suman a la lista: si a alguien se lo quitó del
      // registro no estaría en `mentionables` y su mención se perdería al guardar.
      const content = toStoredText(editing.text.trim(), [
        ...mentionables,
        ...mentionsInText(editing.raw),
      ]);
      await historyNotesApi.patch(editing.noteId, { content });
      setEditing(null);
      reload();
    } catch {
      message.error(es ? "No se pudo editar" : "Could not edit");
    } finally {
      setSavingEdit(false);
    }
  }, [editing, reload, es, mentionables]);

  // Nota por-log: crea sobre el evento (eventId) o responde en el hilo (parentNoteId).
  const submitNoteDraft = useCallback(async () => {
    if (!noteDraft || !noteDraft.text.trim()) return;
    setSavingNoteDraft(true);
    try {
      const content = toStoredText(noteDraft.text.trim(), mentionables);
      if (noteDraft.parentNoteId) {
        await historyNotesApi.reply(noteDraft.parentNoteId, content);
      } else if (noteDraft.eventId) {
        await historyNotesApi.create(module, entityId, {
          content,
          eventId: noteDraft.eventId,
        });
      }
      setNoteDraft(null);
      reload();
    } catch {
      message.error(es ? "No se pudo agregar la nota" : "Could not add the note");
    } finally {
      setSavingNoteDraft(false);
    }
  }, [noteDraft, module, entityId, reload, es, mentionables]);

  /**
   * Compose inline de nota por-log (#139) — Figma 16497-95013: el campo se expande DENTRO
   * del timeline como una entrada más (textarea + ➤), alineado con la columna de la frase.
   * Se envía con el botón (no con Enter: antd se lo queda para elegir la mención), Esc cierra.
   */
  const renderNoteComposer = () => (
    <Flex
      gap={8}
      align="flex-start"
      style={{
        marginLeft: 158,
        marginTop: 4,
        marginBottom: 4,
        background: "#fff",
        border: "1px solid #d9d9d9",
        borderRadius: 6,
        padding: 8,
      }}
    >
      <MentionsInput
        autoFocus
        value={noteDraft?.text ?? ""}
        onChange={(v) => setNoteDraft((prev) => (prev ? { ...prev, text: v } : prev))}
        options={mentionOptions}
        names={mentionNames}
        placeholder={es ? "Escribir la nota… (@ para mencionar)" : "Write the note… (@ to mention)"}
        autoSize={{ minRows: 2, maxRows: 5 }}
        variant="borderless"
        style={{ flex: 1 }}
        onKeyDown={(e) => {
          if (e.key === "Escape") setNoteDraft(null);
        }}
      />
      <Button
        type="text"
        icon={<SendOutlined />}
        loading={savingNoteDraft}
        disabled={!noteDraft?.text.trim()}
        onClick={() => void submitNoteDraft()}
        title={es ? "Agregar nota" : "Add note"}
      />
    </Flex>
  );

  /** Editor de la nota en el lugar: el mismo campo con @ que al crear, con guardar y cancelar. */
  const renderNoteEditor = () => (
    <Flex gap={4} align="flex-start">
      <MentionsInput
        autoFocus
        value={editing?.text ?? ""}
        onChange={(v) => setEditing((prev) => (prev ? { ...prev, text: v } : prev))}
        options={mentionOptions}
        names={mentionNames}
        autoSize={{ minRows: 2, maxRows: 6 }}
        variant="borderless"
        style={{ flex: 1 }}
        onKeyDown={(e) => {
          if (e.key === "Escape") setEditing(null);
        }}
      />
      <Button
        type="text"
        size="small"
        icon={<CheckOutlined />}
        loading={savingEdit}
        disabled={!editing?.text.trim()}
        onClick={() => void saveEdit()}
        title={es ? "Guardar" : "Save"}
      />
      <Button
        type="text"
        size="small"
        icon={<CloseOutlined />}
        onClick={() => setEditing(null)}
        title={es ? "Cancelar" : "Cancel"}
      />
    </Flex>
  );

  /**
   * ⋮ del autor de una nota: editar / fijar / eliminar. Extraído porque lo usan dos lugares —
   * la fila del timeline y la sección de fijadas de arriba. Que esté en las fijadas no es
   * cosmético: al fijarse la nota sale del stream, así que ese ⋮ es el ÚNICO lugar desde donde
   * se la puede desfijar.
   */
  const noteAuthorMenu = (note: {
    noteId: string;
    content: string;
    pinned: boolean;
    visibleOnFront: boolean;
  }) => (
    <Dropdown
      trigger={["click"]}
      menu={{
        items: [
          { key: "edit", icon: <EditOutlined />, label: es ? "Editar" : "Edit" },
          {
            key: "pin",
            icon: <PushpinOutlined />,
            label: note.pinned ? (es ? "Desfijar" : "Unpin") : es ? "Fijar" : "Pin",
          },
          // Publicar / despublicar. Aparece sólo en presupuestos, que es el único front donde el
          // cliente puede verla. Despublicar tiene que estar disponible siempre: si no, publicar
          // sería una decisión sin vuelta atrás.
          ...(permiteNotaVisible
            ? [
                {
                  key: "visible",
                  icon: note.visibleOnFront ? <EyeInvisibleOutlined /> : <EyeOutlined />,
                  label: note.visibleOnFront
                    ? es
                      ? "Ocultar al cliente"
                      : "Hide from customer"
                    : es
                      ? "Mostrar al cliente"
                      : "Show to customer",
                },
              ]
            : []),
          { type: "divider" },
          {
            key: "delete",
            icon: <DeleteOutlined />,
            label: es ? "Eliminar" : "Delete",
            danger: true,
          },
        ],
        onClick: ({ key }) => {
          if (key === "edit")
            setEditing({
              noteId: note.noteId,
              text: toDisplayText(note.content),
              raw: note.content,
            });
          else if (key === "pin") void togglePin(note.noteId, note.pinned);
          else if (key === "visible") void toggleVisible(note.noteId, note.visibleOnFront);
          else if (key === "delete") deleteNote(note.noteId);
        },
      }}
    >
      <MoreOutlined style={{ fontSize: 16, color: "#8c8c8c", cursor: "pointer" }} />
    </Dropdown>
  );

  const renderEntry = (row: HistoryRow, opts: { showActor: boolean; group?: Group }) => {
    const p = row.payload;
    const isNote = row.kind === "note";
    const { icon, color } = actorStyle(p.actorType);
    const g = opts.group;
    const isOpen = g ? expanded.has(g.key) : false;
    const href = originHref(row, module);
    const mod = module === "contact" ? MODULE_META[p.module] : undefined;
    const label = module === "contact" && row.kind === "event" ? entityLabel(p) : null;
    /**
     * Ficha del generador. El backend resuelve `actorContactId` a partir de
     * `(actor_id, store_code)` — `actor_id` es un userId, no un contactId.
     *
     * No linkea en dos casos: cuando no hay id (invitado, sistema, integraciones externas — no son
     * personas navegables) y cuando el actor ES el contacto de esta misma ficha, que es donde ya
     * estás parado. Mismo criterio que `originHref` con la columna de módulo.
     */
    const actorContactId = row.kind === "event" ? p.actorContactId : null;
    const actorHref =
      actorContactId && !(module === "contact" && String(actorContactId) === String(entityId))
        ? `/contacts/${actorContactId}`
        : null;
    const noteAuthorUserId = row.kind === "note" ? p.authorUserId : null;
    const handleNoteAuthorClick = noteAuthorUserId
      ? async () => {
          try {
            const res = await axiosInstance.get(
              `${API_URL_CONTACTS}?filter.userId=${noteAuthorUserId}&limit=1`,
            );
            const contactId = res?.data?.data?.[0]?.contactId;
            if (contactId) navigate(`/contacts/${contactId}`);
          } catch {}
        }
      : undefined;

    const content = (
      <Flex align="flex-start" gap={8} style={{ padding: "3px 0" }}>
        {/* Actor / autor de la nota */}
        <div style={{ width: 150, flexShrink: 0 }}>
          {opts.showActor && (
            <Flex align="center" gap={4}>
              <span style={{ color: isNote ? BRAND : color, fontSize: 12 }}>
                {isNote ? (p.pinned ? <PushpinOutlined /> : <UserOutlined />) : icon}
              </span>
              <Text
                strong
                italic={isNote}
                style={{
                  color: BRAND,
                  fontSize: 14,
                  ...((actorHref || handleNoteAuthorClick) ? { cursor: "pointer" } : {}),
                }}
                ellipsis
                onClick={actorHref ? () => navigate(actorHref) : handleNoteAuthorClick}
                title={(actorHref || handleNoteAuthorClick) ? (es ? "Ir a la ficha" : "Go to record") : undefined}
              >
                {isNote
                  ? `[${p.authorName ?? ""}]`
                  : p.actorType === "guest"
                    ? guestLabel(i18n.language)
                    : p.actorName}
              </Text>
            </Flex>
          )}
        </div>

        {/* Columna de módulo (solo vista transversal de Contactos): tipo + id del registro
            (Presupuesto #<quoteId> / Pedido #<incrementId>). El entityId del evento ya es ese id. */}
        {module === "contact" && (
          <div style={{ width: 180, flexShrink: 0 }}>
            {opts.showActor && mod && (
              <Flex
                vertical
                gap={2}
                onClick={href ? () => navigate(href) : undefined}
                style={{ cursor: href ? "pointer" : "default" }}
                title={href ? (es ? "Ir al registro" : "Go to record") : undefined}
              >
                <Flex align="center" gap={4}>
                  <span style={{ color: "rgba(0,0,0,0.88)", fontSize: 14, display: "inline-flex", alignItems: "center" }}>{mod.icon}</span>
                  <Text style={{ color: "rgba(0,0,0,0.88)", fontSize: 14 }} ellipsis>
                    {es ? mod.es : mod.en}
                  </Text>
                </Flex>
                {label && (
                  <Text style={{ color: BRAND, fontSize: 12 }} ellipsis>
                    {label}
                  </Text>
                )}
              </Flex>
            )}
          </div>
        )}

        {/* Frase */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {isNote && editing?.noteId === p.noteId ? (
            renderNoteEditor()
          ) : (
            <Phrase row={row} viewModule={module} entityId={entityId} />
          )}
        </div>

        {/* Contador de grupo expandible */}
        {g && g.rows.length > 1 && (
          <Button
            size="small"
            type="text"
            onClick={() => toggle(g.key)}
            style={{ fontSize: 12, color: "#595959" }}
          >
            {g.rows.length - 1}{" "}
            {isOpen ? (
              <UpOutlined style={{ fontSize: 10 }} />
            ) : (
              <DownOutlined style={{ fontSize: 10 }} />
            )}
          </Button>
        )}

        {/* Hora + acciones */}
        <Flex
          align="center"
          gap={8}
          justify="flex-end"
          style={{ width: 85, flexShrink: 0 }}
        >
          <Text type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
            {dayjs(row.occurredAt).format("HH:mm")} hs
          </Text>
          {isNote && currentUserId != null && p.authorUserId === currentUserId ? (
            noteAuthorMenu({
              noteId: p.noteId as string,
              content: p.content ?? "",
              pinned: !!p.pinned,
              visibleOnFront: !!p.visibleOnFront,
            })
          ) : !isNote && p.eventId && notes ? (
            <Dropdown
              trigger={["click"]}
              menu={{
                items: [
                  {
                    key: "note",
                    icon: <EditOutlined />,
                    label: es ? "Agregar nota" : "Add note",
                  },
                ],
                onClick: () => setNoteDraft({ eventId: p.eventId as string, text: "" }),
              }}
            >
              <MoreOutlined style={{ fontSize: 16, color: "#8c8c8c", cursor: "pointer" }} />
            </Dropdown>
          ) : (
            <MoreOutlined style={{ fontSize: 16, color: "#8c8c8c" }} />
          )}
        </Flex>
      </Flex>
    );

    if (isNote) {
      const noteId = p.noteId as string;
      // Azul = la está leyendo el cliente; amarillo = interna. Mismo par de colores que el input,
      // para que el operador reconozca de un vistazo qué salió del equipo y qué no.
      const paraCliente = !!p.visibleOnFront;
      return (
        <div
          ref={noteId ? noteFocusRef(noteId) : undefined}
          style={{
            background: paraCliente ? CUSTOMER_NOTE_BG : NOTE_BG,
            border: `1px solid ${
              editing?.noteId === noteId
                ? EDITING_BORDER
                : paraCliente
                  ? CUSTOMER_NOTE_BORDER
                  : NOTE_BORDER
            }`,
            borderRadius: 4,
            padding: "0 8px",
            ...(noteId ? highlightStyle(noteId) : null),
          }}
        >
          {content}
        </div>
      );
    }
    return content;
  };

  /**
   * Una nota del hilo como entrada propia (#139): caja separada con el layout del log
   * — [autor] en la columna del generador (verde bold, como el resto del timeline) +
   * contenido en itálica + hora + el ⋮ para dejar una nota sobre ella.
   *
   * **Sin anidar**: todas las notas del log van al mismo nivel, una debajo de la anterior, en
   * orden de creación — aunque una sea "sobre" otra. Antes las hijas se indentaban como un hilo
   * de chat; el pedido es explícito de que no se vea en cadena.
   *
   * `responder: false` en el hilo de una nota general (D66): ahí contesta sólo el asistente —el
   * backend rechaza la respuesta de una persona—, así que el ⋮ queda sin acción, como el de una
   * entrada del timeline que no tiene ninguna.
   */
  const renderThreadNote = (n: HistoryEventNote, { responder = true } = {}) => (
    <div
      key={n.noteId}
      ref={noteFocusRef(n.noteId)}
      style={{
        background: NOTE_BG,
        border: `1px solid ${NOTE_BORDER}`,
        borderRadius: 4,
        padding: "4px 8px",
        marginBottom: 4,
        ...highlightStyle(n.noteId),
      }}
    >
      <Flex align="flex-start" gap={8}>
        <div style={{ width: 130, flexShrink: 0 }}>
          <Text strong italic ellipsis style={{ fontSize: 12, color: BRAND }}>
            [{n.authorName}]
          </Text>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text italic style={{ fontSize: 12 }}>
            {renderNoteContent(n.content)}
          </Text>
        </div>
        <Text type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
          {dayjs(n.createdAt).format("HH:mm")} hs
        </Text>
        {responder ? (
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: "note",
                  icon: <EditOutlined />,
                  label: es ? "Agregar nota" : "Add note",
                },
              ],
              onClick: () => setNoteDraft({ parentNoteId: n.noteId, text: "" }),
            }}
          >
            <MoreOutlined style={{ fontSize: 16, color: "#8c8c8c", cursor: "pointer" }} />
          </Dropdown>
        ) : (
          <MoreOutlined style={{ fontSize: 16, color: "#8c8c8c" }} />
        )}
      </Flex>
    </div>
  );

  /**
   * Las respuestas de una nota general (D66), debajo de ella y con la misma caja que las notas de
   * un log: planas, en orden de creación. `marginLeft` alinea con la columna de la frase, que en
   * las secciones de fijadas y programadas queda corrida por su padding.
   */
  const renderRespuestasDeNota = (noteId: string, marginLeft = 158) => {
    const respuestas = hilosArmados.respuestas.get(noteId) ?? [];
    // En una charla con el asistente una persona también responde (D66): en cualquier nota del
    // hilo o con «Responder» al pie, y su respuesta lo despierta sin volver a arrobarlo.
    const puedeResponder = !!hilos.get(noteId)?.canReply;
    const enEsteHilo =
      !!noteDraft?.parentNoteId &&
      (noteDraft.parentNoteId === noteId || respuestas.some((r) => r.noteId === noteDraft.parentNoteId));
    if (!respuestas.length && !puedeResponder) return null;
    // El compose ya trae su propia sangría: va fuera del contenedor corrido, al pie del hilo.
    return (
      <>
      <div style={{ marginTop: 4, marginLeft }}>
        {respuestas.map((n) => (
          <div key={n.noteId}>{renderThreadNote(n, { responder: puedeResponder })}</div>
        ))}
        {puedeResponder && !enEsteHilo && (
          <Button
            type="link"
            size="small"
            style={{ padding: 0, fontSize: 12 }}
            onClick={() =>
              setNoteDraft({ parentNoteId: respuestas.at(-1)?.noteId ?? noteId, text: "" })
            }
          >
            {es ? "Responder" : "Reply"}
          </Button>
        )}
      </div>
      {enEsteHilo && renderNoteComposer()}
      </>
    );
  };

  /**
   * Notas por-log de un evento (008 F4) + el compose inline cuando el draft apunta a este log o
   * a alguna de sus notas (#139).
   *
   * Se renderizan **planas**, en el orden de creación que ya devuelve el backend
   * (`notesByEventIds` ordena por `created_at ASC`): una nota sobre otra nota cae naturalmente
   * debajo de la anterior, sin indentación y sin agrupar por `parentNoteId`. La relación
   * padre-hijo sigue existiendo en el dato — solo no se dibuja como cadena.
   */
  const renderEventNotes = (row: HistoryRow) => {
    const notes = row.kind === "event" ? row.payload.notes ?? [] : [];
    const eventId = row.kind === "event" ? row.payload.eventId : undefined;
    const noteIds = new Set(notes.map((n) => n.noteId));
    const composingOnEvent = !!noteDraft?.eventId && noteDraft.eventId === eventId;
    const composingOnNote =
      !!noteDraft?.parentNoteId && noteIds.has(noteDraft.parentNoteId);
    if (!notes.length && !composingOnEvent && !composingOnNote) return null;
    return (
      <div style={{ marginTop: 4 }}>
        {notes.map((n) => (
          <div key={n.noteId}>
            <div style={{ marginLeft: 158 }}>{renderThreadNote(n)}</div>
            {/* El compose queda pegado a la nota sobre la que se está escribiendo. */}
            {noteDraft?.parentNoteId === n.noteId && renderNoteComposer()}
          </div>
        ))}
        {composingOnEvent && renderNoteComposer()}
      </div>
    );
  };

  // Filas → items del Timeline (marcador de día + grupos, rail continuo).
  const timelineItems = useMemo(() => {
    type Item = NonNullable<React.ComponentProps<typeof Timeline>["items"]>[number];
    const items: Item[] = [];
    for (const { day, groups } of days) {
      items.push({
        color: "gray",
        dot: (
          <span
            style={{
              display: "block",
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: "#d9d9d9",
            }}
          />
        ),
        children: (
          <Text strong style={{ fontSize: 10, lineHeight: "18px" }}>
            {dayjs(day).format("DD MMMM, YYYY")}
          </Text>
        ),
      });
      for (const g of groups) {
        const head = g.rows[0];
        const isNote = head.kind === "note";
        items.push({
          color: head.kind === "note"
            ? (head.payload.visibleOnFront ? CUSTOMER_NOTE_BORDER : "#d4b106")
            : actorStyle(head.payload.actorType).color,
          children: (
            <div>
              {renderEntry(head, { showActor: true, group: g })}
              {renderEventNotes(head)}
              {isNote && renderRespuestasDeNota(head.id)}
              {expanded.has(g.key) &&
                g.rows.slice(1).map((r) => (
                  <div key={r.id}>
                    {renderEntry(r, { showActor: false })}
                    {renderEventNotes(r)}
                  </div>
                ))}
            </div>
          ),
        });
      }
    }
    return items;
    // renderEntry depende de expanded/navigate/module/es; days ya depende de rows.
    // noteDraft/savingNoteDraft: el compose inline vive DENTRO del timeline (#139), sin
    // ellos en las deps el campo no aparecería al abrirlo ni reflejaría lo tipeado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, expanded, module, es, noteDraft, savingNoteDraft, mentionOptions, editing, savingEdit, saveEdit, hilosArmados, hilos]);

  const filterChips = module === "contact" && (
    <Flex
      align="center"
      gap={8}
      wrap="wrap"
      style={scrollable
        ? { flexShrink: 0, padding: "8px 16px", borderBottom: "1px solid #f0f0f0" }
        : { marginBottom: 8 }
      }
    >
      <Text style={{ fontSize: 13, color: "#595959", whiteSpace: "nowrap" }}>
        {rows.length} {es ? "actividades" : "activities"}
      </Text>
      <Text style={{ fontSize: 13, color: "#8c8c8c" }}>-</Text>
      <Text style={{ fontSize: 13, color: "#595959", whiteSpace: "nowrap" }}>
        {es ? "Filtros sugeridos:" : "Suggested filters:"}
      </Text>
      <Flex gap={4} wrap="wrap">
        {CONTACT_PILLS.map((p) => (
          <Button
            key={p.value}
            size="small"
            type={pill === p.value ? "primary" : "default"}
            onClick={() => setPill(pill === p.value ? "" : p.value)}
          >
            {p.label}
          </Button>
        ))}
      </Flex>
    </Flex>
  );

  // Compose de notas (OpenSpec 008 Fase 4) — Figma CONTACTOS (16497-95012): textarea + botón
  // enviar (➤) y programar (^). Se renderiza pinneado arriba del timeline (ver returns abajo).
  const composeBox = !notes ? null : (
    <Flex
      gap={8}
      align="flex-start"
      style={scrollable ? { flexShrink: 0, padding: "8px 16px 0" } : { marginBottom: 12 }}
    >
      <div style={{ flex: 1, position: "relative" }}>
        <MentionsInput
          value={compose}
          onChange={setCompose}
          options={mentionOptions}
          names={mentionNames}
          placeholder={es ? "Dejar una nota… (@ para mencionar)" : "Leave a note… (@ to mention)"}
          autoSize={{ minRows: 2, maxRows: 5 }}
          focusBorderColor={visibleOnFront ? CUSTOMER_NOTE_FG : "#fa8c16"}
          style={{
            width: "100%",
            // El color dice quién la va a leer, sin tener que buscar la etiqueta: amarillo = interna
            // (como el resto de las notas del timeline), azul = la ve el cliente.
            background: visibleOnFront ? CUSTOMER_NOTE_BG : NOTE_BG,
            borderColor: visibleOnFront ? CUSTOMER_NOTE_BORDER : NOTE_BORDER,
          }}
        />
        {permiteNotaVisible && (
          <Tooltip
            title={
              visibleOnFront
                ? es
                  ? "La va a ver el cliente en su presupuesto. Click para hacerla interna."
                  : "The customer will see this. Click to make it internal."
                : es
                  ? "Sólo la ve el equipo. Click para mostrársela al cliente."
                  : "Only your team sees this. Click to show it to the customer."
            }
          >
            <span
              onClick={() => setVisibleOnFront((v) => !v)}
              style={{
                position: "absolute",
                right: 10,
                bottom: 6,
                cursor: "pointer",
                userSelect: "none",
                fontSize: 12,
                fontWeight: 500,
                color: visibleOnFront ? CUSTOMER_NOTE_FG : "#d48806",
              }}
            >
              {visibleOnFront
                ? (es ? "Nota al cliente" : "Customer note")
                : (es ? "Nota interna" : "Inter note")}{" "}
              {visibleOnFront ? <EyeOutlined /> : <EyeInvisibleOutlined />}
            </span>
          </Tooltip>
        )}
      </div>
      <Button
        icon={<SendOutlined />}
        loading={sending}
        disabled={!compose.trim()}
        onClick={() => void submitNote()}
        title={es ? "Enviar ahora" : "Send now"}
      />
      <Popover
        open={schedOpen}
        onOpenChange={(o) => setSchedOpen(o && !!compose.trim())}
        trigger="click"
        placement="bottomRight"
        content={
          <Flex vertical gap={8} style={{ width: 240 }}>
            <DatePicker
              showTime={{ format: "HH:mm", showSecond: false }}
              value={schedDate}
              onChange={setSchedDate}
              disabledDate={(d) => !!d && d < dayjs().startOf("day")}
              style={{ width: "100%" }}
            />
            <Button
              type="primary"
              size="small"
              loading={sending}
              disabled={!schedDate || !compose.trim()}
              onClick={() => schedDate && void submitNote(schedDate.toISOString())}
            >
              {es ? "Programar" : "Schedule"}
            </Button>
          </Flex>
        }
      >
        <Button
          // Reloj y no la flecha: la flecha ^ no dice nada de programar, y encima choca
          // visualmente con el ^ del contador de grupo del timeline, que sí es un colapsar.
          // Es el mismo ícono que la insignia "Programada" de la sección de arriba, así que la
          // acción y su resultado se leen como lo mismo.
          icon={<ClockCircleOutlined />}
          disabled={!compose.trim() || sending}
          title={es ? "Programar nota" : "Schedule note"}
        />
      </Popover>
    </Flex>
  );

  /**
   * Notas programadas pendientes: sección propia arriba del timeline, pegada al compose.
   *
   * No van en el stream a propósito — el backend las excluye hasta que vencen, porque hasta
   * entonces no son historia del registro sino algo por hacer. Se muestran primeras porque son
   * lo único de esta pantalla que pide acción; cuando vencen desaparecen de acá y aparecen en su
   * lugar cronológico del timeline. Ordenadas por la más próxima (viene así del API).
   */
  const scheduledBox = notes && scheduled.length > 0 && (
    <Flex
      vertical
      gap={4}
      style={scrollable ? { flexShrink: 0, padding: "8px 16px 0" } : { marginBottom: 12 }}
    >
      {scheduled.map((n) => (
        <div key={n.noteId}>
          <Flex
            ref={noteFocusRef(n.noteId)}
            align="flex-start"
            gap={8}
            style={{
              background: NOTE_BG,
              // Punteado para que se lea distinto de una nota ya publicada: esto todavía no pasó.
              border: `1px dashed ${NOTE_BORDER}`,
              borderRadius: 4,
              padding: "6px 10px",
              ...highlightStyle(n.noteId),
            }}
          >
            <Flex align="center" gap={4} style={{ width: 150, flexShrink: 0 }}>
              <ClockCircleOutlined style={{ color: "#d4b106", fontSize: 12 }} />
              <Text strong style={{ fontSize: 12, color: "#8c6d00", whiteSpace: "nowrap" }}>
                {es ? "Programada" : "Scheduled"}
              </Text>
            </Flex>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text italic style={{ fontSize: 12 }}>
                {renderNoteContent(n.content ?? "")}
              </Text>
              <div>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  [{n.authorName}]
                </Text>
              </div>
            </div>
            <Text type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
              {n.scheduledFor ? dayjs(n.scheduledFor).format("DD/MM/YY HH:mm") : ""} hs
            </Text>
          </Flex>
          {renderRespuestasDeNota(n.noteId, 168)}
        </div>
      ))}
    </Flex>
  );

  /**
   * Notas fijadas: primeras del log, antes de los marcadores de día.
   *
   * No están además en el stream (el backend las excluye con `pinned = false`), así que esta es
   * la única representación de la nota mientras esté fijada — de ahí que lleve el ⋮ del autor:
   * es el único lugar desde donde se puede desfijar.
   */
  const pinnedBox = notes && pinnedNotes.length > 0 && (
    <Flex
      vertical
      gap={4}
      style={scrollable ? { flexShrink: 0, padding: "8px 16px 0" } : { marginBottom: 12 }}
    >
      {pinnedNotes.map((n) => (
        <div key={n.noteId}>
          <Flex
            ref={noteFocusRef(n.noteId)}
            align="flex-start"
            gap={8}
            style={{
              background: n.visibleOnFront ? CUSTOMER_NOTE_BG : NOTE_BG,
              border: `1px solid ${
                editing?.noteId === n.noteId
                  ? EDITING_BORDER
                  : n.visibleOnFront
                    ? CUSTOMER_NOTE_BORDER
                    : NOTE_BORDER
              }`,
              borderRadius: 4,
              padding: "6px 10px",
              ...highlightStyle(n.noteId),
            }}
          >
            <Flex align="center" gap={4} style={{ width: 150, flexShrink: 0 }}>
              <PushpinOutlined style={{ color: "#d4b106", fontSize: 12 }} />
              <Text strong italic ellipsis style={{ fontSize: 12, color: BRAND }}>
                [{n.authorName}]
              </Text>
            </Flex>
            <div style={{ flex: 1, minWidth: 0 }}>
              {editing?.noteId === n.noteId ? (
                renderNoteEditor()
              ) : (
                <Text italic style={{ fontSize: 12 }}>
                  {renderNoteContent(n.content ?? "")}
                </Text>
              )}
            </div>
            <Text type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
              {dayjs(n.createdAt).format("DD/MM/YY HH:mm")} hs
            </Text>
            {currentUserId != null && n.authorUserId === currentUserId ? (
              noteAuthorMenu({
                noteId: n.noteId,
                content: n.content ?? "",
                pinned: true,
                visibleOnFront: !!n.visibleOnFront,
              })
            ) : (
              <MoreOutlined style={{ fontSize: 16, color: "#8c8c8c" }} />
            )}
          </Flex>
          {renderRespuestasDeNota(n.noteId, 168)}
        </div>
      ))}
    </Flex>
  );

  const timelineContent = (
    <>
      {rows.length === 0 && !loading ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Sin actividad registrada"
          style={{ padding: 16 }}
        />
      ) : (
        <Timeline items={timelineItems} style={{ padding: "8px 4px 0" }} />
      )}
      {loading && (
        <div style={{ textAlign: "center", padding: 12 }}>
          <Spin size="small" />
        </div>
      )}
      <div ref={sentinelRef} style={{ height: 1 }} />
    </>
  );

  if (scrollable) {
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        {filterChips}
        {composeBox}
        {scheduledBox}
        {pinnedBox}
        <div
          ref={scrollContainerRef}
          style={{ flex: 1, overflowY: "auto", padding: "8px 16px 16px", minHeight: 0 }}
        >
          {timelineContent}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Buscador (texto o persona) */}
      {externalQ === undefined && (
        <Flex gap={8} style={{ marginBottom: 8 }}>
          <Input.Search
            allowClear
            placeholder="Search..."
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onSearch={(v) => setQ(v.trim())}
            style={{ flex: 1 }}
          />
        </Flex>
      )}
      {filterChips}
      {composeBox}
      {scheduledBox}
      {pinnedBox}
      {timelineContent}
    </div>
  );
};
